import { useEditorContext, useEditorMode } from 'app/state/contexts/EditorContext';
import { useCoverageScope } from 'app/components/editor/coverage/CoverageScopeContext';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Transforms, type Element as SlateElement } from 'slate';
import { ReactEditor, useSlateStatic, type RenderElementProps } from 'slate-react';
import { StyledDataSheet } from 'app/components';
import type { TagColorResolver } from 'shared/data-display/StyledDataSheet';
import { getTagHighlightColor, getTagIndicatorColor } from 'slate/extensions/tag';
import { SeletedTextWrapper } from 'slate/components/Paragraph';
import { AddComment } from 'app/components/editor/sidebar/comments/AddComment';
import { AddTag } from 'app/components/editor/sidebar/tags/AddTag';
import { AiSuggestTags } from 'app/components/editor/sidebar/tags/AiSuggestTags';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { useTagDisplay, useTagSelection } from 'app/state/contexts/TagContext';
import { useCalcPick } from 'app/state/contexts/CalcPickContext';
import { useBlockTagColors, blockTagHighlightStyle } from 'slate/extensions/tag/utils/useBlockTagColors';
import { isNumericText } from 'app/utils/tableCells';
import { BlockTagBadge } from 'slate/extensions/tag/components/BlockTagBadge';
import { useEditorReferences } from 'app/api/documents/useEditorData';
import { useEditorDocument, useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import { showOriginalValueReferenceTable } from 'slate/utils';
import { usePermissions } from 'app/api/permission-queries';
import { useEditorScope } from 'app/state/contexts/EditorScopeContext';
import type { TagBinding } from 'slate/extensions/tag/types/block';

interface SelectionCell {
  value: string;
  tags?: string[];
}

type SelectionContent = SelectionCell[][];

interface CellCoord {
  i?: number;
  j?: number;
}

interface CellSelection {
  start: CellCoord;
  end: CellCoord;
}

interface SelectedCell {
  row: number;
  col: number;
  value: string;
  isNumeric: boolean;
}

interface TaggedCell {
  cellRow: number;
  cellCol: number;
  tagId: string;
}

interface ReferenceContent {
  superId: string;
  revisionId: string;
  selectionContent?: SelectionContent;
}

type TaggableTableElement = SlateElement & {
  uuid: string;
  referenceDataSuperId?: string;
  tags?: TagBinding[];
  coverage?: { rule?: { kind?: string } };
};

interface TaggableTableProps {
  element: TaggableTableElement;
  attributes: RenderElementProps['attributes'];
  children: React.ReactNode;
  withComments?: boolean;
  withTags?: boolean;
}

const EMPTY_SELECTION: CellSelection = { start: {}, end: {} };

const resolveTagColors: TagColorResolver = (opt, data) => ({ highlight: getTagHighlightColor(opt, data), indicator: getTagIndicatorColor(opt, data) });

// Helper to get individual cells from selection
const getSelectedCells = (data: SelectionContent | undefined, selection: CellSelection): SelectedCell[] => {
  if (!data) return [];

  const { start, end } = selection;
  if (start.i === undefined || start.j === undefined || end.i === undefined || end.j === undefined) return [];
  const startRow = Math.min(start.i, end.i);
  const endRow = Math.max(start.i, end.i);
  const startCol = Math.min(start.j, end.j);
  const endCol = Math.max(start.j, end.j);

  const cells: SelectedCell[] = [];
  for (let row = startRow; row <= endRow; row++) {
    for (let col = startCol; col <= endCol; col++) {
      const cell = data[row]?.[col];
      if (cell) {
        cells.push({
          row,
          col,
          value: cell.value,
          isNumeric: isNumericText(cell.value?.toString() ?? ''),
        });
      }
    }
  }
  return cells;
};

export const TaggableTable = (props: TaggableTableProps) => {
  const { element, attributes, children, withComments, withTags } = props;
  const references = useEditorReferences();
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();
  const section = useEditorLiveSection();
  const showOriginalValue = useMemo(() => showOriginalValueReferenceTable(pDocument, section, element), [pDocument, section, element]);
  const [selection, setSelection] = useState<CellSelection>(EMPTY_SELECTION);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { shadowHost } = useEditorContext();
  const slateEditor = useSlateStatic();
  const { taggingSubMode, isTaggingMode } = useEditorMode();
  const { scopeUuids, missingUuids, anchorsVisible } = useCoverageScope();
  const selectTableForCoverage = useCallback(() => {
    if (taggingSubMode !== 'mark-coverage') return;
    try {
      const path = ReactEditor.findPath(slateEditor, element);
      Transforms.select(slateEditor, path);
    } catch (err) {
      console.error('Path is not resolvable', err);
    }
  }, [taggingSubMode, slateEditor, element]);
  const { setIsSidebarOpen } = useSidebar();
  const { highlightedOption, tagDataMap, termHighlightTagIds } = useTagDisplay();
  const { setClickedTagIds, setTableTagSelection, clearTableTagSelection, tableSelection } = useTagSelection();
  const { calcPickMode, toggleTerm } = useCalcPick();

  // Clear selection when clicking outside the table but inside the document
  useEffect(() => {
    if (!shadowHost?.shadowRoot) return;
    const root = shadowHost.shadowRoot;

    const handleClickOutside = (event: Event) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSelection(EMPTY_SELECTION);
      }
    };
    root.addEventListener('mousedown', handleClickOutside);
    return () => {
      root.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shadowHost]);

  const content = useMemo<ReferenceContent | undefined>(() => {
    return references?.find((r) => r.superId === element.referenceDataSuperId) as ReferenceContent | undefined;
  }, [references, element]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // In coverage mode the capture-phase handler selects this table so the
      // marker controls can target it; don't clear that selection here.
      if (taggingSubMode === 'mark-coverage') return;
      const shadowRoot = shadowHost?.shadowRoot as (ShadowRoot & { getSelection?: () => Selection | null }) | null | undefined;
      const domSelection = shadowRoot
        ? shadowRoot.getSelection
          ? shadowRoot.getSelection()
          : shadowRoot.ownerDocument.getSelection()
        : window.getSelection?.();
      domSelection?.removeAllRanges?.();
      if (slateEditor.selection) {
        Transforms.deselect(slateEditor);
      }
    },
    [shadowHost, slateEditor, taggingSubMode]
  );

  // Get cells that have been tagged by reading tags from cell.tags in selectionContent
  const taggedCells = useMemo<TaggedCell[]>(() => {
    if (!content?.selectionContent) return [];
    const cells: TaggedCell[] = [];
    content.selectionContent.forEach((row: SelectionCell[], rowIndex: number) => {
      row.forEach((cell: SelectionCell, colIndex: number) => {
        if (cell.tags && cell.tags.length > 0) {
          // Each cell can have multiple tags, add an entry for each
          cell.tags.forEach((tagId: string) => {
            cells.push({
              cellRow: rowIndex,
              cellCol: colIndex,
              tagId: tagId,
            });
          });
        }
      });
    });
    return cells;
  }, [content?.selectionContent]);

  const handleTagClick = useCallback(
    (tagIds: string[]) => {
      if (!tagIds || tagIds.length === 0) return;
      // Coverage mode: tags are read-only, clicking a tagged cell does nothing.
      if (taggingSubMode === 'mark-coverage') return;
      if (calcPickMode) {
        const conceptId = tagDataMap?.[tagIds[0]]?.conceptId;
        if (!conceptId || conceptId === calcPickMode.parentConceptId) return;
        toggleTerm(conceptId, tagIds[0]);
        return;
      }
      if (setClickedTagIds) {
        setClickedTagIds(tagIds, { fromContent: true });
      }
      if (setIsSidebarOpen) {
        setIsSidebarOpen(8);
      }
    },
    [setClickedTagIds, setIsSidebarOpen, calcPickMode, toggleTerm, tagDataMap, taggingSubMode]
  );

  // Get selected cells data when selection changes
  const selectedCells = useMemo<SelectedCell[]>(() => {
    if (!content?.selectionContent || selection.start?.i === undefined) return [];
    return getSelectedCells(content.selectionContent, selection);
  }, [content, selection]);

  const ownsTableSelectionRef = useRef(false);
  useEffect(() => {
    if (!setTableTagSelection || !clearTableTagSelection) return;
    if (selectedCells.length > 0) {
      ownsTableSelectionRef.current = true;
      setTableTagSelection({
        element,
        referenceDataSuperId: element.referenceDataSuperId,
        cells: selectedCells,
        onTagsCreated: () => setSelection(EMPTY_SELECTION),
      });
    } else if (ownsTableSelectionRef.current) {
      ownsTableSelectionRef.current = false;
      clearTableTagSelection();
    }
  }, [selectedCells, element, setTableTagSelection, clearTableTagSelection]);

  const blockTagBindings = useMemo<TagBinding[]>(() => (Array.isArray(element.tags) ? element.tags : []), [element.tags]);
  const hasBlockTag = blockTagBindings.length > 0;
  const blockTagColors = useBlockTagColors(hasBlockTag ? blockTagBindings : undefined);

  const scope = useEditorScope();
  const isEmbedded = !!scope?.embedd;
  const { documentPermissions } = usePermissions(scope?.documentId ?? '');
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');

  if (!content) {
    return (
      <div {...attributes}>
        <div style={{ userSelect: 'none' }} contentEditable={false}>
          Loading data...
        </div>
        {children}
      </div>
    );
  }

  const hasSelection = selection?.start?.i !== undefined;
  const metadata = {
    'data-element-super-id': element.referenceDataSuperId,
    'data-element-uuid': element.uuid,
    'data-document-revision-id': content.revisionId,
    'data-element-revision-id': `${element.referenceDataSuperId}_${content.revisionId}`,
  };
  const isTableSelected = !!tableSelection?.element && tableSelection.element.uuid === element.uuid;
  const blockTagClass = isTableSelected ? ' taggable-table-wrapper--block-tagged' : '';
  const showBlockTagVisual = hasBlockTag && !isEmbedded && isTaggingMode && blockTagColors && !blockTagColors.isHidden;
  const blockTagStyle = {
    ...(showBlockTagVisual ? blockTagHighlightStyle(blockTagColors) : {}),
    ...(isTableSelected && blockTagColors ? { outlineColor: blockTagColors.indicator } : {}),
  };
  const coverageClass =
    anchorsVisible && element.coverage && element.coverage.rule && element.coverage.rule.kind
      ? ` coverage-anchor coverage-anchor--${element.coverage.rule.kind}`
      : '';
  let scopeClass = '';
  if (element.uuid) {
    if (missingUuids.has(element.uuid)) scopeClass = ' coverage-missing';
    else if (scopeUuids.has(element.uuid)) scopeClass = ' coverage-scope';
  }
  return (
    <div
      {...metadata}
      className={`taggable-table-wrapper${coverageClass}${scopeClass}${blockTagClass}`}
      style={blockTagStyle ?? undefined}
      onMouseDownCapture={selectTableForCoverage}
      onClick={(e: React.MouseEvent) => {
        if (!hasBlockTag || taggingSubMode === 'mark-coverage') return;
        const cell = (e.target as Element).closest && (e.target as Element).closest('td, th');
        if (cell) return;
        setClickedTagIds?.(
          blockTagBindings.map((b) => b.tagId),
          { fromContent: true }
        );
        setIsSidebarOpen?.(8);
      }}
    >
      {hasBlockTag && !isEmbedded && isTaggingMode && (
        <BlockTagBadge
          bindings={blockTagBindings}
          tooltipKey="tag:block.table.tagged"
          canEdit={canEditTags}
          onContinueClick={(firstTagId: string) => {
            setClickedTagIds?.([firstTagId]);
            setIsSidebarOpen?.(8);
          }}
          onSelectTag={(tagId: string) => {
            setClickedTagIds?.([tagId]);
            setIsSidebarOpen?.(8);
          }}
          onOpen={() => {
            setClickedTagIds?.(blockTagBindings.map((b) => b.tagId));
            setIsSidebarOpen?.(8);
          }}
        />
      )}
      <div ref={containerRef} contentEditable={false} onMouseDown={handleMouseDown} className="taggable-table-container">
        <StyledDataSheet
          data={content.selectionContent}
          locale={locale}
          showOriginalValue={showOriginalValue}
          selected={selection}
          onSelect={setSelection}
          className="taggable-table"
          taggedCells={taggedCells}
          onTagClick={handleTagClick}
          withTags={withTags}
          highlightedOption={highlightedOption}
          tagDataMap={tagDataMap}
          calcPickMode={calcPickMode}
          termHighlightTagIds={termHighlightTagIds}
          resolveTagColors={resolveTagColors}
        />
        <SeletedTextWrapper popperRef={containerRef.current} show={hasSelection}>
          <>
            {withComments && containerRef.current ? <AddComment element={element} /> : null}
            {withTags && containerRef.current ? (
              <AddTag
                element={element}
                selectedCells={selectedCells}
                referenceDataSuperId={element.referenceDataSuperId}
                onTagsCreated={() => setSelection(EMPTY_SELECTION)}
              />
            ) : null}
            {withTags && containerRef.current ? (
              <AiSuggestTags
                element={element}
                referenceDataSuperId={element.referenceDataSuperId}
                documentSuperId={pDocument?.superId}
                selectedCells={selectedCells}
              />
            ) : null}
          </>
        </SeletedTextWrapper>
      </div>
      {children}
    </div>
  );
};
